import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../utils/Functions";

const columns = [
  { id: "index", label: "#", align: "left", width: 56, sortable: false },
  { id: "title", label: "Title", align: "left", sortable: true },
  { id: "manufacturerName", label: "ManufacturerName", align: "left", sortable: true },
  { id: "genericName", label: "GenericName", align: "left", sortable: true },
];

const SECONDARY_COLUMNS = columns.filter((column) => column.id !== "index" && column.id !== "title");
const CELL_TRIM_LENGTH = 30;

function TableHeader({ order, orderBy, onSort }) {
  const { t } = useTranslation();

  function handleSort(columnId) {
    if (!onSort) return;
    const isAsc = orderBy === columnId && order === "asc";
    onSort(columnId, isAsc ? "desc" : "asc");
  }

  return (
    <MUI.TableHead>
      <MUI.TableRow
        sx={{
          "& th": {
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "grey.50"
                : "rgba(255,255,255,0.04)",
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.secondary",
            borderBottom: "2px solid",
            borderBottomColor: "divider",
          },
        }}
      >
        {columns.map((column) => (
          <MUI.TableCell
            key={column.id}
            align={column.align}
            sx={{ width: column.width }}
            sortDirection={orderBy === column.id ? order : false}
          >
            {column.id === "index" ? (
              ""
            ) : column.sortable ? (
              <MUI.TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : "asc"}
                onClick={() => handleSort(column.id)}
              >
                {t(column.label)}
              </MUI.TableSortLabel>
            ) : (
              t(column.label)
            )}
          </MUI.TableCell>
        ))}
      </MUI.TableRow>
    </MUI.TableHead>
  );
}

function LoadingRow() {
  return (
    <MUI.TableRow>
      <MUI.TableCell colSpan={columns.length} sx={{ border: 0, py: 6 }}>
        <MUI.Stack alignItems="center" justifyContent="center">
          <MUI.CircularProgress size={28} />
        </MUI.Stack>
      </MUI.TableCell>
    </MUI.TableRow>
  );
}

function EmptyRow({ emptyMessage }) {
  const { t } = useTranslation();

  return (
    <MUI.TableRow>
      <MUI.TableCell colSpan={columns.length} sx={{ border: 0, py: 3 }}>
        <MUI.Typography variant="body1" color={"grayText"}>
          {t(emptyMessage)}
        </MUI.Typography>
      </MUI.TableCell>
    </MUI.TableRow>
  );
}

function PrescriptionRow({ item, index, onRowClick }) {
  return (
    <MUI.TableRow
      hover
      onClick={() => onRowClick(item)}
      sx={{
        cursor: "pointer",
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      <MUI.TableCell sx={{ color: "text.disabled" }}>
        {index + 1}
      </MUI.TableCell>
      <MUI.TableCell>
        <MUI.Tooltip title={item.title}>
          <MUI.Typography variant="body2" sx={{ fontWeight: 600 }}>
            {trimText(item.title, CELL_TRIM_LENGTH)}
          </MUI.Typography>
        </MUI.Tooltip>
      </MUI.TableCell>
      {SECONDARY_COLUMNS.map((column) => (
        <MUI.TableCell key={column.id}>
          <MUI.Typography variant="body2" color="text.secondary">
            {trimText(item[column.id], CELL_TRIM_LENGTH) || "-"}
          </MUI.Typography>
        </MUI.TableCell>
      ))}
    </MUI.TableRow>
  );
}

export default function PrescriptionsTable({
  items,
  isLoading,
  emptyMessage,
  onRowClick,
  order,
  orderBy,
  onSort,
}) {
  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table sx={{ minWidth: 700 }}>
          <TableHeader order={order} orderBy={orderBy} onSort={onSort} />
          <MUI.TableBody>
            {isLoading ? (
              <LoadingRow />
            ) : items.length === 0 ? (
              <EmptyRow emptyMessage={emptyMessage} />
            ) : (
              items.map((item, index) => (
                <PrescriptionRow key={item.id} item={item} index={index} onRowClick={onRowClick} />
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}
